import axios from "axios";
import { toast } from "sonner";

// The base URL for our API. We prefer the *page's own origin* over the
// REACT_APP_BACKEND_URL env var because:
//
// 1. In production the page lives on the customer's custom domain
//    (e.g. https://kanal.cyrusfieldtech.com) while REACT_APP_BACKEND_URL points
//    at the Emergent stage host (https://kanal-frontend-stage.emergent.host).
//    The custom domain proxies `/api/*` to the backend, so we can call the API
//    same-origin via the customer's branded domain. This gives us three things:
//      a) The browser auto-attaches the session cookie set by `/auth/login`,
//         which is required for plain `<img src="/api/...">` tags that cannot
//         set an Authorization header (driver selfies, license/vehicle photos,
//         receipts, courier logos, invoice PDFs, etc.).
//      b) The CORS preflight problem from iter32 (`Access-Control-Allow-Origin: *`
//         paired with credentialed requests) goes away entirely — same-origin
//         requests don't preflight at all for simple GETs and don't need the
//         credentials handshake.
//      c) No `emergent.host` leak in user-visible URLs (share links, "open in
//         new tab" buttons, etc.).
//
// 2. In preview, REACT_APP_BACKEND_URL already equals the page origin, so this
//    is a no-op.
//
// Fallback to BACKEND_URL is only used during SSR or when window is unavailable.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const RESOLVED_BASE = PAGE_ORIGIN || BACKEND_URL || "";
export const API = `${RESOLVED_BASE}/api`;
// Public-facing base URL — use this for ALL constructed URLs (image src, share
// links, raw fetch() calls) so we never leak the Emergent stage hostname in
// production. Same-origin in prod → kanal.cyrusfieldtech.com; same in preview.
export const PUBLIC_BACKEND = RESOLVED_BASE;

export const api = axios.create({
  baseURL: API,
  // Now that requests are same-origin, cookies set by /auth/login flow through
  // automatically. We keep withCredentials off because (a) it's redundant for
  // same-origin and (b) it would re-introduce the wildcard-CORS browser block
  // on the rare cross-origin call.
});

// Also store token in memory/localStorage as fallback since cookies may be blocked cross-site
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 handling — single active session: server returns "Session ended — signed in elsewhere"
// or a generic 401 when token expires. In both cases we clear local state and bounce to /login.
// IMPORTANT: When the user is ALREADY on a public auth page (/login, /forgot-password,
// /reset-password, /pay, etc.) we MUST NOT trigger window.location.assign. Doing so
// aborts any in-flight axios request, which strips `error.response` — and the calling
// code's `formatApiErrorDetail(err.response?.data?.detail)` then renders the generic
// "Something went wrong. Please try again." toast. That's the bug users were seeing
// where reset-password + login showed a fake error message even though the request
// had actually succeeded.
const PUBLIC_AUTH_PATHS = ["/login", "/forgot-password", "/reset-password", "/verify-email", "/pay", "/c/", "/auth/callback", "/receipt/"];
const isOnPublicAuthPage = () => {
  const p = typeof window !== "undefined" ? window.location.pathname : "";
  return PUBLIC_AUTH_PATHS.some((prefix) => p.startsWith(prefix));
};

let isLoggingOut = false;
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const url = error?.config?.url || "";
    // Don't intercept the /auth/me probe used by AuthContext, login attempts,
    // or any auth-flow request — those callers handle their own error UI.
    const isAuthProbe =
      url.includes("/auth/me") ||
      url.includes("/auth/login") ||
      url.includes("/auth/forgot-password") ||
      url.includes("/auth/reset-password") ||
      url.includes("/auth/verify-email");
    if (
      status === 401 &&
      !isAuthProbe &&
      !isLoggingOut &&
      !isOnPublicAuthPage()
    ) {
      isLoggingOut = true;
      localStorage.removeItem("access_token");
      const msg = typeof detail === "string" && detail
        ? detail
        : "Your session has ended. Please sign in again.";
      try { toast.error(msg, { duration: 5000 }); } catch (e) { /* ignore */ }
      // Small delay so the toast renders before redirect
      setTimeout(() => {
        if (!isOnPublicAuthPage()) {
          window.location.assign("/login");
        }
        isLoggingOut = false;
      }, 400);
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
